import React, { ReactElement, useCallback, useEffect, useState } from 'react'

import { Button, Row, SimpleCollapse, Span } from 'components'
import { useLoading, useModal, useMovement } from 'hooks'
import { Entity, CamposVisaoTotal } from 'pages/Supplier/MovMensal/types'
import { MovimentacaoMensalServices } from 'services/Supplier/MovMensal'
import { COLORS, FORMAT, SPACING, TYPOGRAPHY } from 'utils'
import { convertBase64, createArrayCopy, isEqual } from 'utils/functions'

import { VisaoTableAnterior, VisaoTableAtual } from './components'
import { boolean } from 'yup'
import { set } from 'react-hook-form'
import VisaoTotal from '../VisaoTotal/VisaoTotal'

interface CompetenciaIndividualProps {
  handleSalvarValidarVisaoIndividual: () => void
}

const CompetenciaIndividual: React.FC<CompetenciaIndividualProps> = ({
  handleSalvarValidarVisaoIndividual
}) => {
  const handleCompetencia = (value: string) =>
    value?.substring(4, 6) + '/' + value?.substring(0, 4)
  const {
    movState,
    setMovState,
    selectedMovement,
    handleBlock,
    handleError,
    blockIndividualVision,
    dataAtuacaoInvalida,
    blockVerifyIndividual,
    setBlockVerifyIndividual,
    isSomenteCompartilhado,
    isSemMobilizacao,
  } = useMovement()
  const { setLoading } = useLoading()

  const handleFilterEfetivos = (query: string, entity: Entity) => {
    query = query.toLowerCase()

    if (query != '') {
      delete entity.fornecedorId
      delete entity.idMovEfetivo
      delete entity.movimentacaoId
      delete entity.cdContrato

      return Object.keys(entity).some((keys) => {
        if (!entity[keys]) {
          return false
        }

        if ('nuMesAnoCompetencia' === keys) {
          const compFormated = handleCompetencia(String(entity[keys])) //  02/2022
          return compFormated?.includes(query)
        }

        if ('dtAdmissao' === keys) {
          const dtFormated = FORMAT.formatDDMMYYYY(entity[keys])
          return dtFormated?.includes(query)
        }

        if ('dtDemissao' === keys) {
          const dtFormated = FORMAT.formatDDMMYYYY(entity[keys])
          return dtFormated?.includes(query)
        }

        if ('cdCpfPassaporte' === keys && /[a-zA-Z]/.test(query)) {
          return entity[keys].startsWith(query.trim().toUpperCase())
        }

        if ('cdCpfPassaporte' == keys && /[0-9]/.test(query)) {
          return entity[keys]
            .trim()
            .replace('.', '')
            .replace('-', '')
            .startsWith(query.trim().replace('.', '').replace('-', ''))
        }

        if ('cdGenero' === keys) {
          let valor: String = ''
          if ('prefiro não informar'.startsWith(query)) {
            valor = 'o'
          } else {
            valor = query[0]
          }

          return valor.startsWith(entity[keys].toLowerCase().trim())
        }

        if ('cdNacionalidade' === keys) {
          let valor: String = ''

          if ('BRASILEIRO'.startsWith(query)) {
            valor = 'b'
          } else if ('ESTRANGEIRO'.startsWith(query)) {
            valor = 'e'
          }

          return valor.startsWith(entity[keys].toLowerCase().trim())
        }

        if ('cdCategoria' === keys) {
          let valor: String = ''

          if ('ADMINISTRATIVO'.startsWith(query)) {
            valor = 'a'
          } else if ('OPERACIONAL'.startsWith(query)) {
            valor = 'o'
          }

          return valor.startsWith(entity[keys].toLowerCase().trim())
        }

        return entity[keys]?.toString()?.toLowerCase()?.includes(query)
      })
    }
    return entity
  }

  const { showModal } = useModal()

  const convertErrorsToReactElement = (errors: string[]): ReactElement => {
    return (
      <ul>
        {errors.map((errorValue: string) => (
          <li>{errorValue}</li>
        ))}
      </ul>
    )
  }

  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error'>('synced')

  const handleSalvarValidarVisaoIndividualWithRetry = async () => {
    setLoading('loading');
    setSyncStatus('syncing'); // ✅ Adicionar

    try {
      // Implementar timeout
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 30000) // 30 segundos
      );

      const operationPromise = handleSalvarValidarVisaoIndividual();

      await Promise.race([operationPromise, timeoutPromise]);

      setSyncStatus('synced'); // ✅ Sucesso

    } catch (error: any) {
      setLoading('idle');
      setSyncStatus('error'); // ✅ Erro

      if (error.message === 'Timeout') {
        showModal({
          titleModal: 'Timeout',
          type: 'error',
          message: 'A operação demorou muito para responder. Verifique sua conexão e tente novamente.',
          hideButtonCancel: true,
          txtButtonConfirm: 'OK'
        });
      } else {
        // Tratar outros erros
        showModal({
          titleModal: 'Erro de Conectividade',
          type: 'error',
          message: 'Falha na comunicação com o servidor. Os dados podem não ter sido salvos corretamente. Deseja tentar novamente?',
          hideButtonCancel: false,
          txtButtonConfirm: 'Tentar Novamente',
          txtButtonCancel: 'Cancelar',
          // ✅ Usar handleConfirm ao invés de onConfirm
          handleConfirm: () => {
            // Fechar o modal atual e tentar novamente
            handleSalvarValidarVisaoIndividualWithRetry();
          }
        });
      }
    }
  };

  const handleUploadEfetivos = async (e) => {
    setLoading('loading')
    setSyncStatus('syncing') // ✅ Adicionar

    try {
      // ✅ Adicionar timeout também aqui
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 30000)
      );

      const uploadPromise = (async () => {
        const base64 = (await convertBase64(e[0])) as string
        const response = await MovimentacaoMensalServices().uploadEfetivos({
          base64: base64.replace(/data:.*\/.*;base64,/g, ''),
          fornecedorId: selectedMovement.fornecedorId
        })

        if (!response.data.sucesso) {
          throw new Error(
            'Planilha apresenta dados inconsistentes, por favor verifique os dados inseridos'
          )
        }

        if (response.data?.valor?.errorList?.$values?.length > 0) {
          showModal({
            titleModal: 'Erro',
            type: 'error',
            message: convertErrorsToReactElement(
              response.data.valor.errorList.$values as string[]
            ),
            hideButtonCancel: true,
            txtButtonConfirm: 'OK'
          })
          return
        }

        setBlockVerifyIndividual(false)
        handleBlock(true, 'visaoTotal')
        setMovState((old) => ({
          ...old,
          compAtual: [
            ...old.compAtual,
            ...(response.data.valor.colaboradores?.$values ?? [])
          ]
        }))
      })();

      await Promise.race([uploadPromise, timeoutPromise]);
      setSyncStatus('synced') // ✅ Sucesso

    } catch (error: any) {
      setSyncStatus('error') // ✅ Erro
      console.warn(error)

      if (error.message === 'Timeout') {
        showModal({
          titleModal: 'Timeout',
          type: 'error',
          message: 'Upload demorou muito para responder. Verifique sua conexão e tente novamente.',
          hideButtonCancel: true,
          txtButtonConfirm: 'OK'
        })
      } else {
        showModal({
          titleModal: 'Erro',
          type: 'error',
          message: 'Ocorreu um erro ao fazer o upload da planilha',
          hideButtonCancel: true,
          txtButtonConfirm: 'OK'
        })
      }
    } finally {
      setLoading('idle')
    }
  }

  const handleCleanTable = () => {
    setMovState((old) => ({
      ...old,
      compAtual: []
    }))

    handleBlock(true, 'visaoTotal')
  }

  /*
   *
   * INDEX EFETIVO
   * ADICIONA INDEX NO STATE PARA PERMANECER A LINHA
   *
   */

  const handleIndexEfetivos = useCallback(() => {
    const addIndex = (old: Entity[]) =>
      old?.map((el, index) => {
        el.index = index + 1
        return el
      })
    if (movState?.compAtual?.length > 0) {
      const cpy = addIndex(createArrayCopy(movState?.compAtual))
      if (!isEqual(cpy, movState?.compAtual)) {
        setMovState((old) => ({ ...old, compAtual: cpy }))
      }
    }

    if (movState?.compAnterior?.length > 0) {
      const cpy = addIndex(createArrayCopy(movState?.compAnterior))
      if (!isEqual(cpy, movState?.compAnterior)) {
        setMovState((old) => ({ ...old, compAnterior: cpy }))
      }
    }
  }, [movState?.compAtual, movState?.compAnterior])

  useEffect(handleIndexEfetivos, [handleIndexEfetivos])

  /*
   *
   * INDEX EFETIVO
   *
   *
   */
  const conditionsToDisableSaveCheckButton = useCallback((): boolean => {
    const disableButton = true
    const enableButton = false

    const noCurrentCompetence = !movState?.isCompetenciaAtual as boolean
    const notHaveEffectives = (movState?.compAtual?.length === 0) as boolean
    const incompleteFullView = (movState?.visaoTotal === null && movState.compAtual.length > 0) as boolean

    if (incompleteFullView) {
      return enableButton //Se eu por o disable 
    }

    if (
      [
        noCurrentCompetence,
        notHaveEffectives,
        blockVerifyIndividual,
        blockIndividualVision,
        dataAtuacaoInvalida
      ].includes(true)
    ) {
      return disableButton
    }
    return enableButton
  }, [
    movState?.compAtual,
    movState?.isCompetenciaAtual,
    blockIndividualVision,
    blockVerifyIndividual,
    isSomenteCompartilhado,
    movState?.visaoTotal
  ])

  //const [valorFlMovValida, setValorFlMovValida] = useState(movState.visaoTotal?.flMovValida);
  //const handleClick = () => {
  // Atualize o estado aqui
  // Por exemplo, se você quiser alternar o valor
  // setValorFlMovValida(false);
  // handleSalvarValidarVisaoIndividual();
  //};
  //const response = async () => {
  //  setLoading('loading')
  //  await MovimentacaoMensalServices()
  //}
  //useEffect(() => {
  //  response();
  //}, [!valorFlMovValida])
  //outras lógicas que você precisa para salvar e verificar..
  return (
    <SimpleCollapse title="MOVIMENTAÇÃO MENSAL ( VISÃO INDIVIDUAL )">
      {/* ✅ Adicionar indicador de status */}
      {syncStatus === 'error' && (
        <Row marginBottom={SPACING.spacing10}>
          <Span
            text="⚠️ Dados podem estar desatualizados. Recarregue a página."
            color={COLORS.error} // ✅ Usar 'error' ao invés de 'red'
            fontSize={TYPOGRAPHY.bodySize14}
          />
        </Row>
      )}
      {syncStatus === 'syncing' && (
        <Row marginBottom={SPACING.spacing10}>
          <Span
            text="𐄄 Sincronizando dados..."
            color={COLORS.info} // ✅ Usar 'info' ao invés de 'blue'
            fontSize={TYPOGRAPHY.bodySize14}
          />
        </Row>
      )}
      {/* TABLE ANTERIOR */}
      {movState.compAnterior?.length > 0 && (
        <VisaoTableAnterior
          listaAnterior={movState?.compAnterior || []}
          handleFilterEfetivos={handleFilterEfetivos}
        />
      )}

      {/* TABLE ATUAL */}
      {isSemMobilizacao ? (
        <Row>
          <Span
            text="Não há lançamento de efetivo"
            color={COLORS.black}
            fontSize={TYPOGRAPHY.bodySize16}
            marginLeft="auto"
            marginRight="auto"
          />
        </Row>
      ) : (
        <>
          <VisaoTableAtual
            listaAtual={movState?.compAtual || []}
            handleFilterEfetivos={handleFilterEfetivos}
            handleUploadEfetivos={handleUploadEfetivos}
          />
          <Row
            justifyContent="flex-end"
            alignItems="center"
            marginTop={SPACING.spacing20}
          >
            <Button
              text="Limpar"
              variantButton="text"
              marginRight={SPACING.spacing20}
              onClick={handleCleanTable}
              disabled={
                blockIndividualVision ||
                !movState.isCompetenciaAtual ||
                dataAtuacaoInvalida ||
                isSomenteCompartilhado
              }
            />
            <Button
              text={syncStatus === 'syncing' ? "PROCESSANDO..." : "SALVAR E VERIFICAR"}
              onClick={handleSalvarValidarVisaoIndividualWithRetry}
              disabled={conditionsToDisableSaveCheckButton() || syncStatus === 'syncing'}
            />
          </Row>
        </>
      )}
    </SimpleCollapse>
  )
}

export default CompetenciaIndividual